import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NewCatalogService } from '../../new-catalog/new-catalog.service';
import { ProcurementAnalyticsService } from './procurement-analytics.service';
import {
  AnalyticsWarehouseMinimumStock,
  AnalyticsMinimumStockSuggestion,
  AnalyticsMinimumStockSuggestions,
  AnalogueSource
} from './procurement-analytics.types';
import { AssortmentReport } from './procurement-assortment.types';
import { analogueCalculationBlockers } from './procurement-analogue-warnings.util';

type EditorSuggestion = Omit<AnalyticsMinimumStockSuggestion, 'status'> & {
  status: AnalyticsMinimumStockSuggestion['status'] | 'mixed_units' | 'incomplete_sources';
  groupMinimum?: number | null;
  otherMinimum?: number;
};

interface MinimumStockEditorRow extends AnalyticsWarehouseMinimumStock {
  draft: number | null;
  saving: boolean;
  saved: boolean;
  error: string;
  suggestion?: EditorSuggestion;
}

export interface ProductMinimumStockChange {
  productId: number;
  stock: AnalyticsWarehouseMinimumStock;
}

@Component({
  selector: 'app-procurement-minimum-stock',
  templateUrl: './procurement-minimum-stock.component.html',
  styleUrls: ['./procurement-minimum-stock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcurementMinimumStockComponent implements OnChanges, OnDestroy {
  @Input() productId = 0;
  @Input() unit = 'ед.';
  @Input() dateFrom = '';
  @Input() dateTo = '';
  @Input() refreshKey = 0;
  @Input() embedded = false;
  @Input() showSuggestions = true;
  @Input() autoSuggest = false;
  @Input() compact = false;
  @Input() familySources: AnalogueSource[] = [];
  @Input() memberIds: number[] = [];
  @Input() suggestionsReady = true;
  @Input() minimumStockChanges: Record<number, AnalyticsWarehouseMinimumStock> = {};
  @Output() minimumStockSaved = new EventEmitter<ProductMinimumStockChange>();

  expanded = false;
  loading = false;
  loaded = false;
  error = '';
  rows: MinimumStockEditorRow[] = [];
  coverageDays: number | null = 30;
  suggesting = false;
  suggestionError = '';
  familyBlockers: string[] = [];
  suggestions?: Omit<AnalyticsMinimumStockSuggestions, 'rows'> & { rows: EditorSuggestion[] };
  private requests = new Subscription();
  private loadRequest?: Subscription;
  private suggestionRequest?: Subscription;

  constructor(
    private readonly analytics: ProcurementAnalyticsService,
    private readonly catalog: NewCatalogService,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.embedded) this.expanded = true;
    if (changes.minimumStockChanges) this.applySavedMinimums();
    const suggestionContextChanged = changes.productId || changes.dateFrom || changes.dateTo ||
      changes.familySources || changes.memberIds || changes.suggestionsReady;
    if (suggestionContextChanged) this.clearSuggestions();
    if (!changes.productId) {
      if (changes.refreshKey && !changes.refreshKey.firstChange) {
        this.loadRequest?.unsubscribe();
        this.loading = false;
        this.loaded = false;
        if (this.expanded) this.load();
      }
      if (suggestionContextChanged && this.autoSuggest && this.loaded) this.suggest();
      return;
    }
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.rows = [];
    this.loaded = false;
    this.loading = false;
    this.error = '';
    if (this.expanded) this.load();
  }

  ngOnDestroy(): void {
    this.requests.unsubscribe();
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.loaded && !this.loading) this.load();
  }

  load(): void {
    if (!this.productId || this.loading) return;
    this.loadRequest?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.loadRequest = this.analytics.minimumStocks(this.productId).subscribe({
      next: (rows) => {
        this.rows = rows.map((row) => ({
          ...row,
          draft: row.minimumStock,
          saving: false,
          saved: false,
          error: ''
        }));
        this.loading = false;
        this.loaded = true;
        this.applySavedMinimums();
        if (this.autoSuggest && this.showSuggestions) this.suggest();
        this.cd.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.error = 'Не удалось загрузить минимальные остатки. Повторите запрос.';
        this.cd.markForCheck();
      }
    });
    this.requests.add(this.loadRequest);
  }

  private applySavedMinimums(): void {
    for (const row of this.rows) {
      const saved = this.minimumStockChanges[row.warehouseId];
      if (!saved || row.saving) continue;
      // A save in the other card tab must not erase a draft in this editor.
      if (row.draft === row.minimumStock) row.draft = saved.minimumStock;
      row.minimumStock = saved.minimumStock;
      row.quantity = saved.quantity;
    }
  }

  validDraft(row: MinimumStockEditorRow): boolean {
    return (
      row.draft !== null && Number.isInteger(row.draft) && row.draft >= 0 && row.draft <= 1000000
    );
  }

  changeDraft(row: MinimumStockEditorRow, value: number | null): void {
    row.draft = value;
    row.saved = false;
    row.error = '';
  }

  validCoverage(): boolean {
    return (
      this.coverageDays !== null &&
      Number.isInteger(this.coverageDays) &&
      this.coverageDays >= 1 &&
      this.coverageDays <= 365
    );
  }

  changeCoverageDays(value: number | null): void {
    this.coverageDays = value;
    this.clearSuggestions();
  }

  private clearSuggestions(): void {
    this.suggestionRequest?.unsubscribe();
    this.suggesting = false;
    this.suggestionError = '';
    this.suggestions = undefined;
    this.familyBlockers = [];
    for (const row of this.rows) row.suggestion = undefined;
  }

  suggest(): void {
    if (
      this.suggesting ||
      !this.suggestionsReady ||
      !this.loaded ||
      !this.rows.length ||
      !this.productId ||
      !this.dateFrom ||
      !this.dateTo ||
      !this.validCoverage()
    )
      return;
    this.clearSuggestions();
    this.suggesting = true;
    if (this.familySources.length) {
      this.suggestionRequest = this.analytics.assortment(this.productId, {
        dateFrom: this.dateFrom, dateTo: this.dateTo, coverageDays: this.coverageDays!,
        sources: this.familySources, page: 1, pageSize: 1, sortBy: 'netQty', sortDirection: 'desc'
      }).subscribe({
        next: (result) => this.receiveFamilySuggestion(result),
        error: () => {
          this.suggesting = false;
          this.suggestionError = 'Не удалось рассчитать запас группы. Повторите запрос; ручное редактирование доступно.';
          this.cd.markForCheck();
        }
      });
      this.requests.add(this.suggestionRequest);
      return;
    }
    this.suggestionRequest = this.analytics
      .minimumStockSuggestions(this.productId, this.dateFrom, this.dateTo, this.coverageDays!)
      .subscribe({
        next: (result) => {
          this.suggestions = result;
          const byWarehouse = new Map(result.rows.map((row) => [row.warehouseId, row]));
          for (const row of this.rows) row.suggestion = byWarehouse.get(row.warehouseId);
          this.suggesting = false;
          this.cd.markForCheck();
        },
        error: (error) => {
          this.suggesting = false;
          this.suggestionError =
            error.status === 400
              ? 'Проверьте период: нужны только завершённые дни, запас — от 1 до 365 дней.'
              : 'Не удалось рассчитать предложение. Повторите запрос; ручное редактирование доступно.';
          this.cd.markForCheck();
        }
      });
    this.requests.add(this.suggestionRequest);
  }

  private receiveFamilySuggestion(result: AssortmentReport): void {
    const ids = (values: number[]) => [...new Set(values)].sort((a, b) => a - b).join(',');
    if (ids(result.memberIds) !== ids(this.memberIds)) {
      this.suggesting = false;
      this.suggestionError = 'Состав аналогов изменился. Обновите продажи перед расчётом запаса.';
      this.cd.markForCheck();
      return;
    }
    this.familyBlockers = analogueCalculationBlockers(result, this.familySources);
    const rows: EditorSuggestion[] = result.warehouses.map((row) => {
      const otherMinimum = Math.max(0, row.configuredMinimum - row.anchorMinimum);
      const validMinimums = Number.isFinite(otherMinimum) && row.anchorMinimum >= 0;
      return {
        ...row,
        groupMinimum: row.suggestedMinimum,
        otherMinimum,
        suggestedMinimum: row.suggestedMinimum === null || !validMinimums ? null :
          Math.max(0, row.suggestedMinimum - otherMinimum),
        status: !validMinimums ? 'needs_reconciliation' : row.status
      };
    });
    this.suggestions = { ...result, method: 'NET_SALES_COVERAGE_V1', rows };
    const byWarehouse = new Map(rows.map((row) => [row.warehouseId, row]));
    for (const row of this.rows) row.suggestion = byWarehouse.get(row.warehouseId);
    this.suggesting = false;
    this.cd.markForCheck();
  }

  useSuggestion(row: MinimumStockEditorRow): void {
    const value = row.suggestion?.suggestedMinimum;
    if (
      !this.rows.includes(row) ||
      row.saving ||
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > 1000000
    )
      return;
    this.changeDraft(row, value);
  }

  suggestionStatus(suggestion: EditorSuggestion): string {
    switch (suggestion.status) {
      case 'mixed_units':
        return 'У товаров группы разные единицы измерения. Нужна ручная оценка.';
      case 'incomplete_sources':
        return 'Расчёт запаса с аналогами заблокирован. ' + (this.familyBlockers.length
          ? this.familyBlockers.join(' ')
          : 'Проверьте источники и неоднозначные кроссы в составе группы.');
      case 'limited_history':
        return 'Мало наблюдений — проверьте предложение вручную.';
      case 'no_sales':
        return 'Нет отгрузок за период. Минимум нужно оценить вручную.';
      case 'non_positive_net':
        return 'Возвраты не меньше продаж. Минимум нужно оценить вручную.';
      case 'needs_reconciliation':
        return 'Есть спорные документы или продажи без склада. Нужна сверка.';
      case 'out_of_range':
        return 'Расчёт превышает допустимый минимум. Проверьте данные вручную.';
      default:
        return '';
    }
  }

  get groupWarning(): string {
    const suggestion = this.rows[0]?.suggestion;
    return suggestion && ['incomplete_sources', 'mixed_units'].includes(suggestion.status) &&
      this.rows.every((row) => row.suggestion?.status === suggestion.status) ? this.suggestionStatus(suggestion) : '';
  }

  save(row: MinimumStockEditorRow): void {
    if (
      !this.rows.includes(row) ||
      row.saving ||
      !this.validDraft(row) ||
      row.draft === row.minimumStock
    )
      return;
    row.saving = true;
    row.saved = false;
    row.error = '';
    const productId = this.productId;
    this.requests.add(
      this.catalog
        .updateMinimumStock({
          product_id: this.productId,
          warehouse_id: row.warehouseId,
          minimum_stock: row.draft!
        })
        .subscribe({
          next: (saved) => {
            row.minimumStock = saved.minimumStock;
            row.draft = saved.minimumStock;
            row.quantity = saved.quantity;
            row.saving = false;
            row.saved = true;
            this.minimumStockSaved.emit({ productId, stock: saved });
            this.cd.markForCheck();
          },
          error: () => {
            row.saving = false;
            row.error = 'Не удалось сохранить. Повторите попытку.';
            this.cd.markForCheck();
          }
        })
    );
  }

  trackWarehouse(_index: number, row: MinimumStockEditorRow): number {
    return row.warehouseId;
  }
}
